// UNO game logic utilities

/**
 * Check if a card can be played on the discard pile
 * @param {Object} card - Card to check
 * @param {Object} topCard - Top card on discard pile
 * @returns {boolean}
 */
export const canPlayCard = (card, topCard) => {
  if (!topCard || !card) return false;
  
  // Wild cards can always be played (check both type and action properties)
  if (card.type === 'wild' || card.action === 'wild' || card.action === 'wild_draw4') {
    return true;
  }
  
  // Get the effective color of the top card (for wild cards that have been played)
  const topCardColor = topCard.colorName || (topCard.action && topCard.colorName ? topCard.colorName : null);
  
  // Match by color (must match the color name)
  if (card.colorName && topCardColor && card.colorName === topCardColor) {
    return true;
  }
  
  // Match by number (both cards must have numbers and they must match)
  if (card.number !== null && topCard.number !== null && card.number === topCard.number) {
    return true;
  }
  
  // Match by action type (same action on both cards)
  if (card.type === 'action' && topCard.type === 'action' && card.action && topCard.action && card.action === topCard.action) {
    return true;
  }
  
  return false;
};

/**
 * Get all playable cards from a hand
 * @param {Array} hand - Player's hand
 * @param {Object} topCard - Top card on discard pile
 * @returns {Array} - Array of playable card indices
 */
export const getPlayableCards = (hand, topCard) => {
  if (!hand || !topCard) return [];
  return hand
    .map((card, index) => ({ card, index }))
    .filter(({ card }) => {
      // Check if card can be played
      return canPlayCard(card, topCard);
    })
    .map(({ index }) => index);
};

/**
 * Handle action card effects
 * @param {Object} card - Action card played
 * @param {number} currentPlayerIndex - Current player index
 * @param {number} direction - Play direction (1 = clockwise, -1 = counter-clockwise)
 * @param {number} playerCount - Total number of players
 * @returns {Object} - { nextPlayerIndex, newDirection, drawCards, skipTurn }
 */
export const handleActionCard = (card, currentPlayerIndex, direction, playerCount) => {
  let nextPlayerIndex = currentPlayerIndex;
  let newDirection = direction;
  let drawCards = 0;
  let skipTurn = false;
  
  if (!card.action) return { nextPlayerIndex, newDirection, drawCards, skipTurn };
  
  switch (card.action) {
    case 'skip':
      skipTurn = true;
      nextPlayerIndex = (currentPlayerIndex + direction + playerCount) % playerCount;
      break;
      
    case 'reverse':
      newDirection = direction * -1;
      // In 2-player game, reverse acts as skip
      if (playerCount === 2) {
        skipTurn = true;
        nextPlayerIndex = currentPlayerIndex;
      } else {
        nextPlayerIndex = (currentPlayerIndex + newDirection + playerCount) % playerCount;
      }
      break;
      
    case 'draw2':
      drawCards = 2;
      skipTurn = true;
      nextPlayerIndex = (currentPlayerIndex + direction + playerCount) % playerCount;
      break;
      
    case 'wild':
      // No immediate effect, player chooses color
      nextPlayerIndex = (currentPlayerIndex + direction + playerCount) % playerCount;
      break;
      
    case 'wild_draw4':
      drawCards = 4;
      skipTurn = true;
      nextPlayerIndex = (currentPlayerIndex + direction + playerCount) % playerCount;
      break;
      
    default:
      nextPlayerIndex = (currentPlayerIndex + direction + playerCount) % playerCount;
  }
  
  return { nextPlayerIndex, newDirection, drawCards, skipTurn };
};

/**
 * Initialize UNO game (set up discard pile and draw pile)
 * @param {Array} deck - Full shuffled deck
 * @param {number} playerCount - Number of players
 * @returns {Object} - { discardPile, drawPile }
 */
export const initializeUnoGame = (deck, playerCount) => {
  const drawPile = [...deck];
  const discardPile = [];
  
  // Deal cards to players (7 each)
  // This is handled separately, but we need to remove them from draw pile
  const cardsDealt = playerCount * 7;
  
  // Flip top card to start discard pile
  // If it's a wild card, put it back and try again
  let topCard;
  let attempts = 0;
  do {
    if (drawPile.length <= cardsDealt) {
      // Not enough cards, shuffle discard pile back into draw pile
      while (discardPile.length > 1) {
        drawPile.push(...discardPile.splice(1));
      }
    }
    topCard = drawPile.splice(cardsDealt, 1)[0];
    attempts++;
    if (attempts > 100) break; // Safety break
  } while (topCard && (topCard.type === 'wild' || topCard.action === 'wild' || topCard.action === 'wild_draw4'));
  
  if (topCard) {
    discardPile.push(topCard);
  } else if (drawPile.length > cardsDealt) {
    discardPile.push(drawPile.splice(cardsDealt, 1)[0]);
  }
  
  return { discardPile, drawPile };
};

/**
 * Draw a card from the draw pile
 * @param {Array} drawPile - Current draw pile
 * @param {Array} discardPile - Current discard pile (to reshuffle if needed)
 * @returns {Object} - { card, newDrawPile, newDiscardPile }
 */
export const drawCard = (drawPile, discardPile) => {
  // If draw pile is empty, reshuffle discard pile (except top card)
  if (drawPile.length === 0) {
    if (discardPile.length <= 1) {
      // No cards to reshuffle
      return { card: null, newDrawPile: [], newDiscardPile: discardPile };
    }
    
    const topCard = discardPile[discardPile.length - 1];
    const cardsToShuffle = discardPile.slice(0, -1);
    
    // Shuffle
    for (let i = cardsToShuffle.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cardsToShuffle[i], cardsToShuffle[j]] = [cardsToShuffle[j], cardsToShuffle[i]];
    }
    
    drawPile = cardsToShuffle;
    discardPile = [topCard];
  }
  
  const card = drawPile.shift();
  return { card, newDrawPile: drawPile, newDiscardPile: discardPile };
};

/**
 * Set color for wild card
 * @param {Object} wildCard - The wild card
 * @param {string} color - Color to set (red, blue, green, yellow)
 * @returns {Object} - Updated card
 */
export const setWildCardColor = (wildCard, color) => {
  const UNO_COLORS = {
    red: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    yellow: '#FACC15',
  };
  
  return {
    ...wildCard,
    color: UNO_COLORS[color] || wildCard.color,
    colorName: color,
  };
};
